<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useRecaptcha } from '~/api/drupal/useRecaptcha'
import { CbzNewsletterFieldsFragment } from '~/api/graphql/generated'

const { pushError } = useNotifications()
const { t } = useI18n()
const { generateToken, verifyToken } = useRecaptcha()

interface FormFields {
  salutation: string
  firstName: string
  lastName: string
  email: string
  terms: boolean
}

const props = defineProps<{
  entity: CbzNewsletterFieldsFragment
}>()

const isSuccess = ref<boolean>(false)

const config = useRuntimeConfig()

const salutations = [
  { value: '1', label: t('form.newsletterForm.salutationValues.woman') },
  { value: '2', label: t('form.newsletterForm.salutationValues.man') },
  { value: '3', label: t('form.newsletterForm.salutationValues.neutral') },
  { value: '4', label: t('form.newsletterForm.salutationValues.notSpecified') },
]

const showCrossSellingCheckbox = computed(() => {
  return props.entity.fieldShowCrossSelling || false
})

const crossSellingContactAgreement = ref(false)

const requestOptions = computed(() => {
  return {
    institutionId: props.entity.fieldInstitution,
    redirectUrl: props.entity.fieldRedirectionUrl?.url?.path
      ? `${config.public.frontendBaseUrl}${props.entity.fieldRedirectionUrl?.url?.path}`
      : 'https://careum.ch/bestaetigung-newsletter-careum-bildungszentrum',
    templateId: props.entity.fieldTemplateId ? parseInt(props.entity.fieldTemplateId) : 7,
    optIn: props.entity.fieldOptin || false,
    newsletterListId: props.entity.fieldNewsletterListId
      ? parseInt(props.entity.fieldNewsletterListId)
      : parseInt(config.public.newsletterListId),
  }
})

const content = computed(() => {
  return {
    title: props.entity.fieldTitle || '',
    description: props.entity.fieldDescription?.processed || '',
    terms: props.entity.fieldTerms?.processed || '',
  }
})

const newsletterSubmit = async (data: FormFields) => {
  const token = await generateToken('sectionFormRecaptcha')
  if (!token) {
    pushError(t('form.errors.generalError'))
    return
  }

  const recaptchaResult = await verifyToken(token)
  if (!recaptchaResult) {
    pushError(t('form.errors.generalError'))
    return
  }

  const response = await fetch(`${config.public.frontendBaseUrl}/api/brevo/newsletter-subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: data.email,
      attributes: {
        VORNAME: data.firstName,
        NACHNAME: data.lastName,
        ANREDE:
          requestOptions.value.institutionId === 'cbz'
            ? parseInt(data.salutation)
            : salutations.find((s) => s.value === data.salutation)?.label,
        DOI_DATRUM_ANMELDUNG_VIA_NEWSLETTER: new Date().toISOString().split('T')[0],
        OPT_IN: requestOptions.value.optIn,
        CROSS_SELLING_KONTAKT: crossSellingContactAgreement.value,
      },
      includeListIds: [requestOptions.value.newsletterListId],
      templateId: requestOptions.value.templateId,
      redirectionUrl: requestOptions.value.redirectUrl,
      institutionId: requestOptions.value.institutionId,
    }),
  }).then((response) => response.json())
  if (response === true) {
    isSuccess.value = true
  } else {
    pushError(t('form.errors.generalError'))
  }
}
</script>
<template>
  <SectionSpecial :title="content.title" :sub-title="content.description">
    
<FormKitLazyProvider config-file="true">
<div class="form-wrapper">
      <p v-if="isSuccess">{{ t('form.newsletterForm.success') }}</p>
      <FormKit v-else type="form" :actions="false" :incomplete-message="false" @submit="newsletterSubmit">
        <ShopNotifications />
        <p>{{ t('form.newsletterForm.sectionHelper') }}</p>
        <div class="wrapper full">
          <Select
            id="cbz-newsletter-salutation"
            name="salutation"
            :required="true"
            :label="t('form.newsletterForm.salutation')"
            :options="salutations"
          />
        </div>
        <div class="wrapper">
          <Input
            id="cbz-newsletter-firstName"
            name="firstName"
            :required="true"
            :label="t('form.newsletterForm.firstName')"
          />
          <Input
            id="cbz-newsletter-lastName"
            name="lastName"
            :required="true"
            :label="t('form.newsletterForm.lastName')"
          />
        </div>
        <div class="wrapper full">
          <Email id="cbz-newsletter-email" name="email" :required="true" :label="t('form.newsletterForm.email')" />
        </div>

        <div class="wrapper full">
          <Checkbox
            id="cbz-newsletter-cross-selling"
            v-show="showCrossSellingCheckbox"
            v-model="crossSellingContactAgreement"
            name="crossSellingContactAgreement"
            :label="t('form.newsletterForm.crossSellingContactAgreement')"
            :html-label="true"
            :required="false"
          />
          <Checkbox
            id="cbz-newsletter-terms"
            name="terms"
            :label="content.terms"
            :required="true"
            :html-label="true"
            :validation-messages="{ accepted: t('form.newsletterForm.termsError') }"
          />
        </div>
        <div class="button-wrapper">
          <Button primary slide type="submit">
            {{ t('form.newsletterForm.submit') }}
          </Button>
        </div>
      </FormKit>
    </div>
</FormKitLazyProvider>

  </SectionSpecial>
</template>
<style scoped lang="scss">
.form-wrapper {
  max-width: 640px;
}
.button-wrapper {
  text-align: right;
}
.wrapper {
  display: grid;
  gap: var(--form-spacing);
  margin-bottom: var(--form-spacing);
  grid-template-columns: 1fr 1fr;
  &.full {
    grid-template-columns: 1fr;
  }
}
:deep(.html-label) {
  * {
    margin: 0;
    @include f-18;
  }
}
</style>
